import { Link } from "react-router-dom";
import { ArrowDown, ArrowRight, ArrowUp, Scale, ShieldOff, SplitSquareVertical } from "lucide-react";

import {
  AgreementRateTrendChart,
  ExceptionsByTierChart,
  OverrideDirectionChart,
  OverrideRateTrendChart,
  ReasonCodeBarChart,
  ReasonCodeTrendChart,
} from "../components/ExceptionsCharts";
import { FilterBar } from "../components/FilterBar";
import { PageHeader } from "../components/PageHeader";
import { Sparkline } from "../components/Sparkline";
import { StatCard } from "../components/StatCard";
import { StepShell, getStep } from "../components/StepShell";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../ui/card";
import { Skeleton } from "../../../ui/skeleton";
import { ToggleGroup, ToggleGroupItem } from "../../../ui/toggle-group";
import { useExceptionsOverridesSummary } from "../lib/api/exceptions-overrides";
import type { SegmentFilter } from "../lib/api/types";
import { useDashboardSearch } from "../lib/useDashboardSearch";
import { cn } from "../lib/utils";

const SUBTITLE =
  "Every departure from credit policy or Athena's recommendation, visible for the first time since May 25, 2026.";

function TrendLabel({
  current,
  previous,
  downIsGood = true,
}: {
  current: number;
  previous: number;
  downIsGood?: boolean;
}) {
  const delta = current - previous;
  const down = delta < 0;
  const good = downIsGood ? down : !down;
  const Icon = down ? ArrowDown : ArrowUp;

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 text-xs font-medium tabular-nums",
        good ? "text-amber" : "text-muted-foreground",
      )}
    >
      <Icon className="size-3" />
      {Math.abs(delta).toFixed(1)} pts vs previous period
    </span>
  );
}

function ReconciliationBar({
  candidateCount,
  confirmedCount,
}: {
  candidateCount: number;
  confirmedCount: number;
}) {
  const total = Math.max(1, candidateCount + confirmedCount);
  return (
    <div className="mt-3 space-y-2">
      <div className="flex h-2 w-full overflow-hidden bg-secondary">
        <div
          className="h-full bg-neutral-inactive"
          style={{ width: `${(candidateCount / total) * 100}%` }}
        />
        <div
          className="h-full bg-primary"
          style={{ width: `${(confirmedCount / total) * 100}%` }}
        />
      </div>
      <div className="flex items-center gap-3 text-[0.7rem] text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <span className="size-2 bg-neutral-inactive" />
          Candidate
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="size-2 bg-primary" />
          Confirmed
        </span>
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <Card className="border-neutral-inactive/40">
      <CardContent className="flex flex-col items-center gap-3 px-6 py-16 text-center">
        <ShieldOff className="size-8 text-neutral-inactive" />
        <p className="text-base font-semibold text-neutral-inactive">
          No data — not yet live on Athena
        </p>
        <p className="max-w-xl text-sm leading-relaxed text-muted-foreground">
          Exceptions and overrides are captured from the moment an office goes live. Select the live
          office or &ldquo;All Offices&rdquo; to see this data.
        </p>
      </CardContent>
    </Card>
  );
}

const SEGMENTS: { value: SegmentFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "PG", label: "PG" },
  { value: "Corp", label: "Corp" },
];

export function ExceptionsOverrides() {
  const { range, office, compare, segment, update } = useDashboardSearch();

  const { data, isPending, isError, error } = useExceptionsOverridesSummary(segment, {
    ...(range ? { range } : {}),
    ...(office ? { office } : {}),
    ...(compare != null ? { compare } : {}),
  });

  const setSegment = (value: string) => {
    if (!value) return;
    update({ segment: value as SegmentFilter });
  };

  const segmentSuffix =
    segment === "all" ? "" : segment === "PG" ? " · PG only" : " · Corp only";

  /**
   * The suppression note for one field, or undefined if that field is measured normally.
   *
   * Driven by the response rather than by a constant here on purpose: the day real Athena
   * output lands, the backend stops sending caveats and every card on this page turns back
   * on with no frontend change. Hardcoding the suppression would leave a permanent em dash
   * long after the data was fixed.
   */
  const caveatFor = (field: string) =>
    data?.caveats?.find((c) => c.field === field)?.note;

  return (
    <StepShell step={getStep("exceptions-overrides")}>
      <PageHeader title="Exceptions and Overrides" subtitle={SUBTITLE} />
      <FilterBar />

      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-border bg-card p-3">
        <span className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
          <SplitSquareVertical className="size-4 text-primary" />
          Segment
        </span>
        <ToggleGroup
          type="single"
          value={segment}
          onValueChange={setSegment}
          variant="outline"
          size="sm"
          aria-label="PG / Corp segment"
        >
          {SEGMENTS.map((s) => (
            <ToggleGroupItem key={s.value} value={s.value} className="px-4">
              {s.label}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
        <span className="text-xs text-muted-foreground">
          Applies to Reason Codes and Exceptions by Tier — the trend charts always show both.
        </span>
      </div>

      {isError ? (
        <Card>
          <CardContent className="p-6 text-sm text-decline" role="alert">
            Could not load exceptions data: {(error as Error).message}
          </CardContent>
        </Card>
      ) : isPending || !data ? (
        <div className="space-y-4">
          <Skeleton className="h-[132px] rounded-lg" />
          <Skeleton className="h-[360px] rounded-lg" />
        </div>
      ) : !data.dataAvailable ? (
        <EmptyState />
      ) : (
        <>
          {/* Any field the backend could not honestly measure says so here, once, above the
              cards — rather than each suppressed card quietly showing 0. A 0% override rate
              beside dataAvailable=true asserts "no overrides occurred", which is a much
              stronger claim than "this is not measurable". */}
          {caveatFor("overrideRate") ? (
            <div className="mb-4 border border-border bg-secondary/40 px-4 py-3">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Not measurable on current data
              </p>
              <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                {caveatFor("overrideRate")}
              </p>
              <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
                Affected: {data.caveats.map((c) => c.field).join(", ")}. The Not-Rated Rate,
                reconciliation counts and exceptions-by-tier below are measured normally.
              </p>
            </div>
          ) : null}

          {/* Section 1 — Top stat row */}
          <div className="grid gap-4 md:grid-cols-3">
            <StatCard
              label={`Override Rate${segmentSuffix}`}
              icon={<Scale className="size-4" />}
              // An em dash rather than 0% when suppressed: the number is unknown, not zero.
              value={
                caveatFor("overrideRate")
                  ? "—"
                  : `${
                      segment === "PG"
                        ? data.overrideRate.currentPg
                        : segment === "Corp"
                          ? data.overrideRate.currentCorp
                          : data.overrideRate.current
                    }%`
              }
              trend={
                caveatFor("overrideRate") ? null : (
                  <TrendLabel
                    current={data.overrideRate.current}
                    previous={data.overrideRate.previousPeriod}
                  />
                )
              }
              hint={
                caveatFor("overrideRate")
                  ? "Athena's recommendation column carries no signal on this data."
                  : `PG ${data.overrideRate.currentPg}% · Corp ${data.overrideRate.currentCorp}%`
              }
            />
            <StatCard
              label="Not-Rated Rate"
              value={`${data.notRatedRate.current}%`}
              trend={
                <TrendLabel
                  current={data.notRatedRate.current}
                  previous={data.notRatedRate.previousPeriod}
                />
              }
              hint="Applications Athena could not score — falling as the model matures."
            />
            <StatCard
              label="Reconciliation"
              value={
                <span className="text-xl">
                  {data.reconciliation.candidateCount} candidate ·{" "}
                  {data.reconciliation.confirmedCount} confirmed
                </span>
              }
              hint={`${data.reconciliation.totalCount} exceptions captured this period — reconciliation lags capture.`}
            />
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <div className="md:col-span-1">
              <Card>
                <CardContent className="p-4">
                  <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Override rate trend
                  </p>
                  <Sparkline data={data.overrideRate.sparkline} width={160} />
                </CardContent>
              </Card>
            </div>
            <div className="md:col-span-1">
              <Card>
                <CardContent className="p-4">
                  <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Not-rated trend
                  </p>
                  <Sparkline data={data.notRatedRate.sparkline} width={160} />
                </CardContent>
              </Card>
            </div>
            <div className="md:col-span-1">
              <Card>
                <CardContent className="p-4">
                  <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Candidate vs confirmed
                  </p>
                  <ReconciliationBar
                    candidateCount={data.reconciliation.candidateCount}
                    confirmedCount={data.reconciliation.confirmedCount}
                  />
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Section 2 — Override Rate Trend */}
          <Card>
            <CardHeader>
              <CardTitle>Override Rate Trend</CardTitle>
              <CardDescription>
                Share of decisions where a credit officer overrode Athena — PG and Corp shown
                separately.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <OverrideRateTrendChart series={data.overrideRateTrend} />
              <p className="text-xs leading-relaxed text-muted-foreground">
                As this falls, the case for trusting Athena on clear-cut deals gets stronger.
              </p>
            </CardContent>
          </Card>

          {/* Section 3 — Override Direction */}
          <Card>
            <CardHeader>
              <CardTitle>Override Direction</CardTitle>
              <CardDescription>
                Which way the override went, month by month.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <OverrideDirectionChart series={data.overrideDirectionTrend} />
              <p className="text-xs leading-relaxed text-muted-foreground">
                Two different signals, not one — approving a decline is a credit risk question,
                declining an approval is a revenue question.
              </p>
            </CardContent>
          </Card>

          {/* Section 4 — Reason Codes */}
          <Card>
            <CardHeader>
              <CardTitle>Reason Codes{segmentSuffix}</CardTitle>
              <CardDescription>
                Current-period volume beside the trend for the three most common reasons.
                Placeholder labels until the derived reason-code table is live.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid gap-4 xl:grid-cols-2">
                <ReasonCodeBarChart data={data.reasonCodeCurrentPeriod} />
                <ReasonCodeTrendChart series={data.reasonCodeTop3Trend} />
              </div>
              <p className="text-xs leading-relaxed text-muted-foreground">
                Watch whether a specific reason is becoming more common, not just which reason is
                currently largest.
              </p>
            </CardContent>
          </Card>

          {/* Section 5 — Exceptions by Tier */}
          <Card>
            <CardHeader>
              <CardTitle>Exceptions by Tier{segmentSuffix}</CardTitle>
              <CardDescription>
                Each tier split into candidate (unreconciled) and confirmed exceptions.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <ExceptionsByTierChart data={data.exceptionsByTier} />
              <Link
                to="/underwriting-exceptions/policy-compliance"
                className="inline-flex items-center gap-1.5 text-xs font-medium text-primary underline-offset-4 hover:underline"
              >
                View rule-level breakdown
                <ArrowRight className="size-3.5" />
              </Link>
            </CardContent>
          </Card>

          {/* Section 6 — Agreement Rate Trend */}
          <Card>
            <CardHeader>
              <CardTitle>Agreement Rate Trend</CardTitle>
              <CardDescription>
                How often the final decision matched Athena&rsquo;s recommendation — PG vs Corp.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <AgreementRateTrendChart series={data.agreementRateTrend} />
              <p className="text-xs leading-relaxed text-muted-foreground">
                A lighter view for now — the full breakdown by segment and ticket size lives on
                Agreement Rate &amp; Credit Culture, the next page but one.
              </p>
            </CardContent>
          </Card>
        </>
      )}
    </StepShell>
  );
}
